import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { WalletCards, Save, Loader2, Image, ChevronLeft } from "lucide-react";
import AdminNavbar from "../../components/layout/AdminNavbar";
import api from "../../api/axios";
import SalarySlipDocument from "../../components/admin/SalarySlipDocument";
import authorisedSignature from "../../assets/authorised-signature.png";
import { buildPeriodFromKey } from "../../utils/salarySlipUtils";

const Field = ({ label, children }) => (
    <label className="space-y-1.5">
        <span className="block text-sm font-bold text-[var(--text-primary)]">{label}</span>
        {children}
    </label>
);

const SalarySlipSettings = () => {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const mockEmployee = { employeeId: "EMP-001", name: "John Doe", designation: "Software Engineer", department: "Engineering", panNumber: "ABCDE1234F", bankDetails: { bankName: "HDFC Bank", accountNumber: "1234567890", ifscCode: "HDFC0001234" }, joiningDate: "2023-01-15T00:00:00Z" };
    const mockPeriodBase = buildPeriodFromKey("2026-08", settings?.payCycle || {startDay: 25});
    const mockPeriod = { ...mockPeriodBase, salarySegments: [{ amount: 50000 }], gross: 50000, totalDed: 1800, net: 49700, totalExpenses: 1500, slipConfig: { showEmployeeDetails: true, showPeriodDetails: true, showPaymentDetails: true, showEarnings: true, showDeductions: true, showAttendanceSummary: true, showPaymentInfo: true } };
// payMonth: "August 2026", payPeriod: "25 Jul 2026 - 24 Aug 2026", payDate: "25 August 2026", periodStart: new Date("2026-07-25"), periodEnd: new Date("2026-08-24"), workDays: 31, lopDays: 0, leaveTakenDays: 1, presentDays: 30, salarySegments: [{ amount: 50000 }], gross: 50000, totalDed: 1800, net: 49700, totalExpenses: 1500, slipConfig: { showEmployeeDetails: true, showPeriodDetails: true, showPaymentDetails: true, showEarnings: true, showDeductions: true, showAttendanceSummary: true, showPaymentInfo: true } };
    const mockExpenses = [{ purpose: "Internet Reimbursement", amount: 1500 }];
    const signatureSrc = typeof authorisedSignature === "string" && /^https?:\/\//i.test(authorisedSignature) ? authorisedSignature : window.location.origin + authorisedSignature;


    useEffect(() => {
        document.title = "Salary Slip Settings | Savrion Technologies";
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await api.get("/api/settings");
            setSettings(res.data);
        } catch (err) {
            toast.error("Failed to load settings.");
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/api/admin/settings", {
                settings: {
                    ...settings,
                }
            });
            toast.success("Salary slip settings saved successfully!");
        } catch (err) {
            toast.error("Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[var(--bg-primary)]">
                <Loader2 size={32} className="animate-spin text-[var(--accent-primary)]" />
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] font-sans text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 min-w-0 w-full p-4 md:p-6 lg:ml-0 overflow-y-auto">
                <div className="mx-auto max-w-[1600px] grid xl:grid-cols-[minmax(500px,1fr)_auto] gap-8">
                    <div className="space-y-6">
                    <div>
                        <h1 className="text-3xl font-black tracking-tight flex items-center gap-3">
                            <WalletCards className="text-[var(--accent-primary)]" size={32} />
                            Salary Slip Settings
                        </h1>
                        <p className="mt-2 text-sm text-[var(--text-secondary)]">
                            Configure global parameters, logic, and visuals for employee salary slips.
                        </p>
                    </div>

                    <form onSubmit={saveSettings} className="space-y-6">
                        <section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-8 shadow-sm">
    <h2 className="text-xl font-bold mb-6 text-[var(--text-primary)]">Pay Period Configuration</h2>
    <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Cycle Start Day of Month">
            <select
                value={settings?.payCycle?.startDay ?? 25}
                onChange={(e) => {
                    const start = Number(e.target.value);
                    const end = start === 1 ? 31 : (start - 1 || 24);
                    setSettings({ ...settings, payCycle: { ...settings.payCycle, startDay: start, endDay: end } });
                }}
                className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold outline-none cursor-pointer"
            >
                <option value={1}>1st of Month (1st to 30th/31st)</option>
                <option value={5}>5th of Month</option>
                <option value={10}>10th of Month</option>
                <option value={15}>15th of Month</option>
                <option value={16}>16th of Month</option>
                <option value={20}>20th of Month</option>
                <option value={21}>21st of Month</option>
                <option value={25}>25th of Month (25th to 24th)</option>
                <option value={26}>26th of Month (26th to 25th)</option>
            </select>
        </Field>
        <Field label="Calculated Cycle End Day">
            <div className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)]/60 px-4 py-3 text-sm font-semibold text-[var(--text-secondary)]">
                {Number(settings?.payCycle?.startDay) === 1
                    ? "Last Day of Month (30th/31st)"
                    : `${Number(settings?.payCycle?.startDay || 25) - 1}th of Next Month`}
            </div>
        </Field>
    </div>
</section>

<section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-8 shadow-sm">
                            <h2 className="text-xl font-bold mb-6 text-[var(--text-primary)]">Company Details</h2>
                            <div className="grid gap-6 sm:grid-cols-2">
                                <Field label="Company Name">
                                    <input
                                        type="text"
                                        value={settings?.salarySlip?.companyName || ""}
                                        onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, companyName: e.target.value } })}
                                        className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold outline-none"
                                    />
                                </Field>
                                <Field label="Authorized Signatory Name">
                                    <input
                                        type="text"
                                        value={settings?.salarySlip?.authorizedSignatory || ""}
                                        onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, authorizedSignatory: e.target.value } })}
                                        className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold outline-none"
                                    />
                                </Field>
                                <Field label="Payment Date Type">
    <select
        value={settings?.salarySlip?.paymentDateType || "offset"}
        onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, paymentDateType: e.target.value } })}
        className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold outline-none"
    >
        <option value="offset">Relative to Period End</option>
        <option value="month_end">End of Pay Month</option>
        <option value="month_start">Start of Next Month</option>
        <option value="custom_date">Custom Date</option>
    </select>
</Field>
{(!settings?.salarySlip?.paymentDateType || settings?.salarySlip?.paymentDateType === "offset") && (
    <Field label="Offset Days (after period end)">
        <input
            type="number"
            value={settings?.salarySlip?.paymentDateOffset || 0}
            onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, paymentDateOffset: parseInt(e.target.value, 10) || 0 } })}
            className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold outline-none"
        />
    </Field>
)}
{settings?.salarySlip?.paymentDateType === "custom_date" && (
    <Field label="Day of Month (1-31)">
        <input
            type="number"
            min="1"
            max="31"
            value={settings?.salarySlip?.paymentCustomDate || 1}
            onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, paymentCustomDate: parseInt(e.target.value, 10) || 1 } })}
            className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-3 text-sm font-semibold outline-none"
        />
    </Field>
)}
                                <Field label="Company Address">
                                    <textarea
                                        value={settings?.salarySlip?.address || ""}
                                        onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, address: e.target.value } })}
                                        rows="4"
                                        className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 text-sm font-semibold outline-none resize-none"
                                    ></textarea>
                                </Field>
                            </div>
                        </section>

                        <section className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-[24px] p-8 shadow-sm">
                            <h2 className="text-xl font-bold mb-6 text-[var(--text-primary)]">Images & Logos</h2>
                            <div className="grid gap-6 sm:grid-cols-2">
                                <Field label="Salary Slip Logo">
                                    <div className="flex flex-col gap-3">
                                        {settings?.salarySlip?.logoUrl ? (
                                            <div className="relative border border-[var(--border-color)] rounded-xl bg-white p-4 flex items-center justify-center min-h-[100px]">
                                                <img
                                                    src={settings.salarySlip.logoUrl}
                                                    alt="Slip Logo"
                                                    style={{ height: `${settings?.salarySlip?.logoSize || 64}px` }}
                                                    className="object-contain"
                                                />
                                            </div>
                                        ) : (
                                            <div className="border border-dashed border-[var(--border-color)] rounded-xl bg-[var(--bg-primary)] flex items-center justify-center min-h-[100px] text-[var(--text-secondary)] text-sm">
                                                No logo set. Falls back to company logo.
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center gap-3">
                                            <label className="flex-1 cursor-pointer bg-[var(--bg-primary)] border border-[var(--border-color)] text-center py-2 rounded-lg text-sm font-semibold hover:bg-[var(--border-color)] transition-all">
                                                Upload Logo
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        if (e.target.files[0]) {
                                                            const reader = new FileReader();
                                                            reader.onload = (ev) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, logoUrl: ev.target.result } });
                                                            reader.readAsDataURL(e.target.files[0]);
                                                        }
                                                    }}
                                                    className="hidden"
                                                />
                                            </label>
                                            <button type="button" className="px-4 py-2 text-red-500 font-semibold text-sm rounded-lg hover:bg-red-50" onClick={() => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, logoUrl: "" } })}>Clear</button>
                                        </div>

                                        {/* LOGO SIZE SLIDER */}
                                        <div className="flex flex-col gap-2 bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-xl p-4">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-bold text-[var(--text-primary)]">Logo Size</span>
                                                <span className="text-sm font-black text-[var(--accent-primary)]">{settings?.salarySlip?.logoSize || 64}px</span>
                                            </div>
                                            <input
                                                type="range"
                                                min="30"
                                                max="150"
                                                step="2"
                                                value={settings?.salarySlip?.logoSize || 64}
                                                onChange={(e) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, logoSize: Number(e.target.value) } })}
                                                className="w-full accent-[var(--accent-primary)] cursor-pointer"
                                            />
                                            <div className="flex justify-between text-xs text-[var(--text-secondary)] font-semibold">
                                                <span>Small</span>
                                                <span>Medium</span>
                                                <span>Large</span>
                                            </div>
                                        </div>
                                    </div>
                                </Field>

                                <Field label="Signature Image">
                                    <div className="flex flex-col gap-3">
                                        {settings?.salarySlip?.signatureUrl ? (
                                            <div className="relative border border-[var(--border-color)] rounded-xl bg-white p-4 flex items-center justify-center min-h-[100px]">
                                                <img src={settings.salarySlip.signatureUrl} alt="Signature" className="h-16 object-contain" />
                                            </div>
                                        ) : (
                                            <div className="border border-dashed border-[var(--border-color)] rounded-xl bg-[var(--bg-primary)] flex items-center justify-center min-h-[100px] text-[var(--text-secondary)] text-sm">
                                                No signature set. Falls back to default.
                                            </div>
                                        )}
                                        
                                        <div className="flex items-center gap-3">
                                            <label className="flex-1 cursor-pointer bg-[var(--bg-primary)] border border-[var(--border-color)] text-center py-2 rounded-lg text-sm font-semibold hover:bg-[var(--border-color)] transition-all">
                                                Upload Signature
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={async (e) => {
                                                        if (e.target.files[0]) {
                                                            const reader = new FileReader();
                                                            reader.onload = (ev) => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, signatureUrl: ev.target.result } });
                                                            reader.readAsDataURL(e.target.files[0]);
                                                        }
                                                    }}
                                                    className="hidden"
                                                />
                                            </label>
                                            <button type="button" className="px-4 py-2 text-red-500 font-semibold text-sm rounded-lg hover:bg-red-50" onClick={() => setSettings({ ...settings, salarySlip: { ...settings.salarySlip, signatureUrl: "" } })}>Clear</button>
                                        </div>
                                    </div>
                                </Field>
                            </div>
                        </section>

                        <div className="flex justify-end pt-4">
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex items-center gap-2 rounded-xl bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] px-8 py-3 text-sm font-black text-white shadow-lg transition-all disabled:opacity-50"
                            >
                                {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                                Save Settings
                            </button>
                        </div>
                    </form>
                    </div>
                    <div className="space-y-6 xl:sticky xl:top-0 h-fit max-w-full overflow-hidden">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold text-[var(--text-primary)]">Live Preview</h2>
                        </div>
                        <div className="border border-[var(--border-color)] rounded-[24px] overflow-hidden shadow-sm bg-gray-100 p-6 overflow-x-auto">
                            <div className="min-w-[800px] transform origin-top-left xl:scale-75 2xl:scale-90 transition-transform bg-white shadow-lg border border-gray-200">
                                <SalarySlipDocument companySettings={settings?.salarySlip} employee={mockEmployee} period={mockPeriod} approvedExpenses={mockExpenses} signatureSrc={signatureSrc} />
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SalarySlipSettings;

